/* eslint-disable */
import config from "appConfig";

const theory: string = `
<h3 class=sectionHead><a id=x1-10001></a>Binaural Impulse Response</h3>
<figure class=figure>
  <p>
    <img alt="Figure 1. Flow diagram of Construction of BRIR." src="${config.ASSETS_URL}/03/16/theory/figure-1.svg">
    <figcaption class=caption><span class=id>Figure 1. Flow diagram of the construction of a BIR.</span></figcaption>
</figure>
<p>
  Binaural Impulse Responses (BIR) from a source to a receiver are based on transfer functions from source to the listener. Independently of the ingredients in the transfer functions such as free-field propagation, transmission, reflection, etc., an effective wave front arrives at the listener’s head from a certain direction of incidence. Therefore, a set of head related impulse responses (HRIRs) in time domain or the corresponding head related transfer functions (HRTFs) in frequency domain are to be applied in order to create a correct spatial perception. To apply HRTF filters, we multiply them with the transfer function as shown <a href=# data-img-alt="Figure 1. Flow diagram of Construction of BRIR." data-img-src="${config.ASSETS_URL}/03/16/theory/figure-1.svg">Figure 1</a> for the simple case of a free field sound propagation between the source and the receiver. In this case, the amplitude of sound pressure due to the energy decrease by the
  <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
    <mfrac>
      <mrow>
        <mn>1</mn>
      </mrow>
      <mrow>
        <msub>
          <mrow>
            <mi>r</mi>
          </mrow>
          <mrow>
            <mi>i</mi>
          </mrow>
        </msub>
      </mrow>
    </mfrac>
  </math>
  distance law between source and receiver is required to be included in filter chain. Also, the absolute phase is included by applying a Dirac function
  <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
    <mi>𝜃</mi>
    <mo class=MathClass-open>(</mo>
    <mi>t</mi>
    <mo class=MathClass-bin>−</mo>
    <mfrac>
      <mrow>
        <msub>
          <mrow>
            <mi>r</mi>
          </mrow>
          <mrow>
            <mi>i</mi>
          </mrow>
        </msub>
      </mrow>
      <mrow>
        <mi>c</mi>
      </mrow>
    </mfrac>
    <mo class=MathClass-close>)</mo>
  </math>
  .  <span class=cite>[<span class=cmbx-10>1</span>]</span>
<h3 class=sectionHead><a id=x1-20002></a>HRTF Databases</h3>
<p>HRTF Databases include data set for several positions around a dummy head or human heads. Mostly, HRTF datasets are available with resolution of 5<span class=tcrm-1000>° </span>in azimuth and elevation directions. These HRTFs are considered far-field conditions for sound sources in distances from at least 2m.
<p>
<h3 class=sectionHead><a id=x1-30003></a>Headphone Equalization</h3>
<p>
  For presentation of binaural signals to the listeners, headphones are easy-to-use, as they provide the necessary separation of the left and right channels. The most common used type is a free-field equalized headphone, where the headphones are calibrated to create the same frequency response as a loudspeaker in front in the free field. This equalisation, however, is not yet perfect for individual listeners because of the individual HRTFs and ear canal properties. Transfer functions of headphones can also be measured individually, and corresponding correction filters are calculated by inverting the recorded transfer function as shown in <a href=# data-img-alt="Figure 2. Arrangement for reproducing binaural signals." data-img-src="${config.ASSETS_URL}/03/16/theory/figure-2.svg">Figure 2</a>. In this way, the frequency response with an individually equalized headphone will be flat with regard to an incident plane wave, thus leading to an undistorted perception of binaural sound from any direction. Futher information can be found in <span class=cite>[<span class=cmbx-10>2</span>]</span>.
  <figure class=figure>
    <a id=x1-30012></a>
<p><img alt="Figure 2. Arrangement for reproducing binaural signals." src="${config.ASSETS_URL}/03/16/theory/figure-2.svg"><figcaption class=caption><span class=id>Figure 2. Arrangement for reproducing binaural signals.</span></figcaption></figure>
<h3 class=likesectionHead>Glossary</h3>
<dl>
  <dt>BIR
  <dd>Binaural Impulse Response
</dl>
<h3>References</h3>
<dl>
    <dt>[1]</dt><dd>J. Blauert. Spatial Hearing. <i>The psychophysics of human sound localization.</i> MIT Press, Camebridge, Massachusetts, revised edition, 1997.</dd>
    <dt>[2]</dt><dd>Z. Schärer and A. Lindau. Evaluation of equalization methods for binaural signals. undefined, 2009.</dd>
</dl>
`;
/* eslint-enable */

export default theory;
